import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import VendedorSelect from '../../components/VendedorSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import comisionesService, {
  DocumentoLiquidableDto,
  DocumentosLiquidablesResult,
  getComisionErrorMessage,
  importeBaseDocumento,
  labelImporteBase,
} from '../../services/comisionesService';
import { formatCurrency } from '../../utils/formatters';
import { formatFechaComision as formatFechaDisplay } from './liquidacionComisionesFormat';
import Can from '../../components/Can';

const LiquidacionComisiones: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro();

  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [fechaEmision, setFechaEmision] = useState(comisionesService.todayIsoDate());

  const [preview, setPreview] = useState<DocumentosLiquidablesResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [liquidando, setLiquidando] = useState(false);

  const rows = preview?.documentos ?? [];
  const comisionBase = preview?.comisionBase;

  const totalComision = useMemo(() => {
    return rows
      .filter((r) => selectedIds.has(r.nroFormulario))
      .reduce((acc, r) => acc + (Number(r.impComision) || 0), 0);
  }, [rows, selectedIds]);

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(rows.map((r) => r.nroFormulario).filter((id) => id > 0)));
  };

  const handleBuscar = async () => {
    if (!nroVendedor || nroVendedor <= 0) {
      enqueueSnackbar('Seleccioná un vendedor', { variant: 'warning' });
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá el rango de fechas', { variant: 'warning' });
      return;
    }
    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha Desde no puede ser mayor que Hasta', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const result = await comisionesService.getDocumentosLiquidables({
        nroVendedor,
        desde: fechaDesde,
        hasta: fechaHasta,
        nroSuc: filtroNroSuc != null && filtroNroSuc > 0 ? filtroNroSuc : null,
      });
      const valid = (result.documentos ?? []).filter((d) => Number(d.nroFormulario) > 0);
      setPreview({ ...result, documentos: valid });
      setSelectedIds(new Set(valid.map((d) => d.nroFormulario)));
      if (valid.length === 0) {
        enqueueSnackbar('No hay comprobantes liquidables para el filtro indicado', {
          variant: 'info',
        });
      }
    } catch (error) {
      setPreview(null);
      setSelectedIds(new Set());
      enqueueSnackbar(getComisionErrorMessage(error, 'Error al buscar documentos liquidables'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (!nroVendedor || nroVendedor <= 0) {
      enqueueSnackbar('Seleccioná un vendedor', { variant: 'warning' });
      return;
    }
    if (!filtroNroSuc || filtroNroSuc <= 0) {
      enqueueSnackbar(
        'Seleccioná la sucursal del comprobante X a generar (no puede ser “Todas”)',
        { variant: 'warning' },
      );
      return;
    }
    if (!fechaEmision) {
      enqueueSnackbar('Indicá la fecha de emisión de la liquidación', { variant: 'warning' });
      return;
    }
    if (selectedIds.size === 0) {
      enqueueSnackbar('Seleccioná al menos un comprobante', { variant: 'warning' });
      return;
    }

    try {
      setLiquidando(true);
      const result = await comisionesService.liquidar({
        nroVendedor,
        fechaEmision,
        nroSuc: filtroNroSuc,
        nroFormularios: Array.from(selectedIds),
      });

      if (!result.exitoso) {
        enqueueSnackbar(result.mensaje?.trim() || 'No se pudo liquidar la comisión', {
          variant: 'error',
        });
        return;
      }

      const nroFc = result.nroFormularioComision;
      enqueueSnackbar(
        result.mensaje?.trim() ||
          (result.formularioDisplay
            ? `Comisión liquidada: ${result.formularioDisplay}`
            : 'Comisión liquidada correctamente.'),
        { variant: 'success' },
      );

      setPreview((prev) => {
        if (!prev) return null;
        const restantes = prev.documentos.filter((r) => !selectedIds.has(r.nroFormulario));
        return {
          ...prev,
          documentos: restantes,
          cantidadFilas: restantes.length,
          subtotalComision: restantes.reduce((acc, r) => acc + (Number(r.impComision) || 0), 0),
        };
      });
      setSelectedIds(new Set());

      if (nroFc && nroFc > 0) {
        navigate(`/compras/facturas/${nroFc}`);
      }
    } catch (error) {
      enqueueSnackbar(getComisionErrorMessage(error, 'Error al confirmar la liquidación'), {
        variant: 'error',
      });
    } finally {
      setLiquidando(false);
    }
  };

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const baseLabel = labelImporteBase(comisionBase);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ gap: 1 }}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nueva liquidación de comisiones
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/ventas/liquidacion-comisiones')}
                sx={{ textTransform: 'none' }}
              >
                Volver al listado
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Al confirmar se genera la Factura Interna X al proveedor y cuenta de comisión del
              vendedor.
            </Typography>
          </Stack>

          <Alert severity="info" sx={{ mb: 3 }}>
            El vendedor debe tener proveedor asociado y cuenta contable de comisión. La sucursal al
            liquidar es la del comprobante X (obligatoria; no usar “Todas”).
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <VendedorSelect
                label="Vendedor"
                value={nroVendedor}
                onChange={(nro) => setNroVendedor(nro)}
                allowEmpty
                emptyOptionLabel="Seleccionar…"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FiltroSucursalVentaSelect
                value={filtroNroSuc}
                onChange={setFiltroNroSuc}
                loading={loadingSucursales}
                sucursales={sucursales}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Fecha Desde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Fecha Hasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? undefined : <SearchIcon />}
                onClick={() => void handleBuscar()}
                disabled={loading || liquidando}
                sx={{ textTransform: 'none', height: 56, borderRadius: 2, fontWeight: 500 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar'}
              </Button>
            </Grid>
          </Grid>

          {preview && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {preview.nombreVendedor && (
                <Chip
                  size="small"
                  label={`Vendedor: ${preview.nombreVendedor}${
                    preview.comisionPorc != null ? ` (${preview.comisionPorc}%)` : ''
                  }`}
                />
              )}
              {preview.comisionBase && (
                <Chip size="small" label={`Base: ${preview.comisionBase}`} variant="outlined" />
              )}
              {preview.nroProveedor != null && preview.nroProveedor > 0 && (
                <Chip
                  size="small"
                  color={preview.nroProveedor ? 'default' : 'warning'}
                  label={`Prov. ${preview.nroProveedor}${
                    preview.nombreProveedor ? ` — ${preview.nombreProveedor}` : ''
                  }`}
                  variant="outlined"
                />
              )}
              {(!preview.nroProveedor || preview.nroProveedor <= 0) && (
                <Chip size="small" color="warning" label="Sin proveedor asociado" />
              )}
              {preview.nroCuentaLiquidacion != null && preview.nroCuentaLiquidacion > 0 ? (
                <Chip
                  size="small"
                  label={`Cta. ${preview.nroCuentaLiquidacion}${
                    preview.nombreCuentaLiquidacion
                      ? ` — ${preview.nombreCuentaLiquidacion}`
                      : ''
                  }`}
                  variant="outlined"
                />
              ) : (
                <Chip size="small" color="warning" label="Sin cuenta de comisión" />
              )}
            </Stack>
          )}

          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', mb: 3, overflowX: 'auto' }}
          >
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.size > 0 && !allSelected}
                      onChange={(_, checked) => toggleAll(checked)}
                      disabled={rows.length === 0 || loading || liquidando}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Nro. interno
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {baseLabel}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Comisión
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Sin comprobantes. Completá filtros y buscá.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: DocumentoLiquidableDto) => (
                    <TableRow key={row.nroFormulario} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(row.nroFormulario)}
                          onChange={() => toggleRow(row.nroFormulario)}
                          disabled={liquidando}
                        />
                      </TableCell>
                      <TableCell>{formatFechaDisplay(row.fechaEmision)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.formularioDisplay?.trim() || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.nombreCliente?.trim() ||
                          (row.nroCliente != null ? `#${row.nroCliente}` : '—')}
                      </TableCell>
                      <TableCell align="right">{row.nroFormulario}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(importeBaseDocumento(row, comisionBase))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(row.impComision ?? 0)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total comisión a liquidar:{' '}
                <Box component="span" sx={{ color: '#1976d2' }}>
                  {formatCurrency(totalComision)}
                </Box>
              </Typography>
              {preview != null && selectedIds.size === rows.length && rows.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Subtotal API: {formatCurrency(preview.subtotalComision)} · {preview.cantidadFilas}{' '}
                  fila{preview.cantidadFilas === 1 ? '' : 's'}
                </Typography>
              )}
              {selectedIds.size > 0 && selectedIds.size !== rows.length && (
                <Typography variant="body2" color="text.secondary">
                  {selectedIds.size} comprobante{selectedIds.size === 1 ? '' : 's'} seleccionado
                  {selectedIds.size === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                label="Fecha emisión liquidación"
                type="date"
                value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Fecha de la Factura X"
                sx={{ minWidth: 220 }}
              />
              <Can permiso="ventas.liquidacion-comisiones.nuevo">
              <Button
                variant="contained"
                color="primary"
                disabled={selectedIds.size === 0 || liquidando || loading}
                onClick={() => void handleConfirmar()}
                sx={{ textTransform: 'none', px: 3, height: 56, borderRadius: 2, fontWeight: 500, alignSelf: { sm: 'flex-start' } }}
              >
                {liquidando ? <CircularProgress size={24} color="inherit" /> : 'Confirmar liquidación'}
              </Button>
              </Can>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LiquidacionComisiones;
